import React, { useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Dropdown,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  ProgressIndicator,
  Spinner,
  TextField,
} from '@fluentui/react';
import { getCrmOptions, planCrmSync, syncCrmTarget } from '../api/prospectingApi';

function requestId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `crm-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function todayLocal() {
  try {
    return new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Europe/Warsaw', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());
  } catch (error) { return new Date().toISOString().slice(0, 10); }
}

export default function CrmSyncDialog({ token, target, onDismiss, onChanged }) {
  const open = Boolean(target);
  const [plan, setPlan] = useState(null);
  const [options, setOptions] = useState(null);
  const [ownerId, setOwnerId] = useState(null);
  const [createActivity, setCreateActivity] = useState(true);
  const [dueDate, setDueDate] = useState(todayLocal());
  const [priority, setPriority] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(0);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setLoading(true); setError(null); setResults([]); setDone(0); setPlan(null);
    Promise.all([
      getCrmOptions(token, {}, controller.signal),
      planCrmSync(token, target, controller.signal),
    ]).then(([opts, p]) => {
      setOptions(opts); setPlan(p);
      const users = opts?.users || [];
      setOwnerId(users[0]?.id ?? null);
      const priorities = opts?.priorities || [];
      setPriority(priorities[0]?.id ?? null);
    }).catch((e) => {
      if (e.name !== 'AbortError') setError(e.message);
    }).finally(() => setLoading(false));
    return () => controller.abort();
  }, [open, token, target]);

  const eligible = useMemo(() => (plan?.targets || []).filter((item) => !item.skip_reason), [plan]);
  const skipped = useMemo(() => (plan?.targets || []).filter((item) => item.skip_reason), [plan]);

  async function execute() {
    if (!ownerId || running || eligible.length === 0) return;
    if (createActivity && !dueDate) { setError('Wybierz dzień kontaktu.'); return; }
    setRunning(true); setError(null); setResults([]); setDone(0);
    const queue = [...eligible];
    const collected = [];
    let completed = 0;

    async function worker() {
      while (queue.length) {
        const item = queue.shift();
        try {
          const response = await syncCrmTarget(token, {
            entity_id: item.entity_id,
            product: target.product,
            owner_id: Number(ownerId),
            create_activity: createActivity,
            due_date: dueDate,
            priority: priority === null ? null : Number(priority),
            request_id: requestId(),
          });
          const result = response?.result || response;
          collected.push({ entity_id: item.entity_id, name: item.name, status: response?.status || result?.status || 'done', response });
        } catch (e) {
          collected.push({ entity_id: item.entity_id, name: item.name, status: 'error', message: e.message });
        } finally {
          completed += 1;
          setDone(completed);
          setResults([...collected]);
        }
      }
    }

    // Remote creates are intentionally serialized. Pipedrive does not provide
    // generic idempotency for every POST, and bulk group actions must never race
    // organization/label/activity creation.
    await worker();
    setRunning(false);
    if (onChanged) onChanged();
  }

  const failures = results.filter((r) => r.status === 'error' || r.status === 'needs_review');
  const syncWarnings = results.flatMap((r) => {
    const warnings = r.response?.result?.warnings || r.response?.warnings || [];
    return warnings.map((warning, index) => ({
      ...warning,
      entity_id: r.entity_id,
      name: r.name,
      key: `${r.entity_id}-${warning.code || 'warning'}-${index}`,
    }));
  });

  return (
    <Dialog
      hidden={!open}
      onDismiss={running ? undefined : onDismiss}
      dialogContentProps={{
        type: DialogType.largeHeader,
        title: target?.title || 'Dodaj / synchronizuj z Pipedrive',
        subText: target?.subtitle || 'ONE dopasuje organizację po Numerze Rejestrowym, a dla RSPO użyje starego pola RSPO wyłącznie jako fallback.',
      }}
      modalProps={{ isBlocking: running }}
      minWidth={620}
    >
      {loading ? <Spinner label="Sprawdzam zakres i Pipedrive…" /> : null}
      {error ? <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar> : null}
      {plan ? (
        <>
          <div className="prospecting-crm-plan-summary">
            <strong>{eligible.length} do synchronizacji</strong>
            <span>{skipped.length ? ` · ${skipped.length} pominiętych` : ''}</span>
          </div>
          <div className="prospecting-crm-plan-list">
            {(plan.targets || []).slice(0, 10).map((item) => (
              <div key={item.entity_id} className={item.skip_reason ? 'is-skipped' : ''}>
                <span>{item.name}</span>
                <small>{item.crm_linked ? 'CRM: powiązane · Ponowny kontakt' : 'CRM: brak powiązania · Pierwszy kontakt'}{item.skip_reason ? ` · pominięte: ${item.skip_reason === 'own_customer' ? 'nasza placówka' : 'scoring 0'}` : ''}</small>
              </div>
            ))}
            {(plan.targets || []).length > 10 ? <small>…i jeszcze {(plan.targets || []).length - 10}</small> : null}
          </div>
          <Dropdown
            label="Handlowiec"
            selectedKey={ownerId}
            options={(options?.users || []).map((u) => ({ key: Number(u.id), text: u.name }))}
            onChange={(_, option) => setOwnerId(option?.key ?? null)}
          />
          <Checkbox
            label="Utwórz / wykorzystaj zadanie kontaktu"
            checked={createActivity}
            onChange={(_, checked) => setCreateActivity(Boolean(checked))}
          />
          {createActivity ? (
            <div className="prospecting-crm-task-fields">
              <TextField label="Dzień kontaktu" type="date" value={dueDate} onChange={(_, value) => setDueDate(value || '')} />
              <Dropdown
                label="Priorytet"
                selectedKey={priority}
                placeholder={(options?.priorities || []).length ? 'Wybierz priorytet' : 'Pipedrive nie zwrócił słownika priorytetów'}
                options={(options?.priorities || []).map((p) => ({ key: Number(p.id), text: p.label }))}
                onChange={(_, option) => setPriority(option?.key ?? null)}
              />
            </div>
          ) : null}
          {running || results.length ? (
            <ProgressIndicator
              label={running ? 'Synchronizacja Pipedrive' : 'Synchronizacja zakończona'}
              description={`${done} / ${eligible.length}${syncWarnings.length ? ` · ${syncWarnings.length} ostrzeżeń` : ''}${failures.length ? ` · ${failures.length} wymaga uwagi` : ''}`}
              percentComplete={eligible.length ? done / eligible.length : 0}
            />
          ) : null}
          {syncWarnings.slice(0, 4).map((warning) => (
            <MessageBar key={warning.key} messageBarType={MessageBarType.warning}>
              {warning.name}: {warning.message || 'Synchronizacja zakończyła się z ostrzeżeniem.'}
            </MessageBar>
          ))}
          {failures.slice(0, 4).map((failure) => (
            <MessageBar key={`${failure.entity_id}-${failure.status}`} messageBarType={MessageBarType.warning}>
              {failure.name}: {failure.message || failure.response?.error || failure.response?.status || 'operacja wymaga sprawdzenia'}
            </MessageBar>
          ))}
        </>
      ) : null}
      <DialogFooter>
        <PrimaryButton disabled={loading || running || !ownerId || eligible.length === 0} onClick={execute} text={running ? 'Synchronizuję…' : 'Dodaj / synchronizuj'} />
        <DefaultButton disabled={running} onClick={onDismiss} text="Zamknij" />
      </DialogFooter>
    </Dialog>
  );
}
